import React, { useState, useEffect, useRef } from 'react';
import { Header } from '../components/Header';
import { PaymentModal } from '../components/PaymentModal';
import { CancellationModal } from '../components/CancellationModal';
import { RefundModal } from '../components/RefundModal';
import { useToast } from '../components/Toast';
import { Package, Clock, Truck, CheckCircle, XCircle, CreditCard, Loader, Printer, ChefHat, ArrowRight, Phone, User, DollarSign, RefreshCw, Calendar, ChevronLeft, ChevronRight, Search, Download, Filter, X } from 'lucide-react';
// MIGRATION FIREBASE: Utilise Firebase direct au lieu de l'API Python
import { ordersService } from '../services/firebaseService';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Helper: Vérifier si une commande est payée (inclut paiement fidélité)
//
// FIX 2026-08-20 (bug "commande payée en fidélité" sans débit réel) :
// l'ancien "Cas 2" marquait payée TOUTE commande dont payment_method valait
// 'loyalty'/'cashback'/'fidelite', quel que soit le solde réellement utilisé —
// une commande à 45€ avec 2€ de fidélité appliquée passait "payée" sans que les
// 43€ restants soient jamais encaissés. Le mode de paiement seul ne prouve rien ;
// seul le total réellement dû (calculé côté serveur dans createOrder) le prouve.
// On ne garde donc que : payment_status='paid' (carte, vérifié via Stripe) OU
// total ramené à 0€ par la fidélité (fiable depuis que createOrder ne débite/écrit
// la commande que si le solde fidélité couvre bien ce montant, cf. functions/index.js).
const isOrderPaid = (order) => {
  // Cas 1: payment_status explicitement 'paid'
  if (order.payment_status === 'paid') return true;

  // Cas 2: Total ramené à 0€ par la fidélité = intégralement couvert, donc payé
  if ((order.total === 0 || order.total === '0' || order.total === 0.00) && order.loyalty_used > 0) {
    return true;
  }

  return false;
};

// Configuration des colonnes Kanban
const COLUMNS = [
  {
    id: 'new',
    status: 'new',
    label: '🆕 Nouvelles',
    color: 'from-red-500 to-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-300',
    headerBg: 'bg-gradient-to-r from-red-500 to-red-600',
    icon: Package,
    nextAction: '🔥 Commencer'
  },
  {
    id: 'in_preparation',
    status: 'in_preparation',
    label: '👨‍🍳 En Préparation',
    color: 'from-orange-500 to-amber-500',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
    headerBg: 'bg-gradient-to-r from-orange-500 to-amber-500',
    icon: ChefHat,
    nextAction: '✅ Prête'
  },
  {
    id: 'ready',
    status: 'ready',
    label: '✅ Prêtes',
    color: 'from-purple-500 to-violet-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-300',
    headerBg: 'bg-gradient-to-r from-purple-500 to-violet-600',
    icon: CheckCircle,
    nextAction: '🎉 Terminé'
  },
  {
    id: 'completed',
    status: 'completed',
    label: '🎉 Terminées',
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-300',
    headerBg: 'bg-gradient-to-r from-green-500 to-emerald-600',
    icon: CheckCircle,
    nextAction: null
  },
  {
    id: 'cancelled',
    status: 'cancelled',
    label: '❌ Annulées',
    color: 'from-gray-500 to-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-300',
    headerBg: 'bg-gradient-to-r from-gray-500 to-gray-600',
    icon: XCircle,
    nextAction: null
  }
];

export const OrdersManagement = () => {
  const toast = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [updatingOrder, setUpdatingOrder] = useState(null);

  // États pour recherche et filtres
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');

  // Ref pour le nombre de nouvelles commandes (évite de recréer le polling interval)
  const previousNewOrdersCountRef = useRef(0);

  // Ref pour éviter que le polling écrase les modifications optimistes en cours
  const lastUpdateTimestampRef = useRef(0);

  // Sélecteur de date - par défaut aujourd'hui
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const isToday = selectedDate === new Date().toISOString().split('T')[0];
  const isTodayRef = useRef(isToday);

  // Mettre à jour la ref quand isToday change
  useEffect(() => {
    isTodayRef.current = isToday;
  }, [isToday]);

  useEffect(() => {
    loadOrders();
  }, [selectedDate]);

  // Polling pour nouvelles commandes - seulement si on regarde aujourd'hui
  // Utilise des refs pour éviter de recréer l'interval à chaque changement
  useEffect(() => {
    const interval = setInterval(async () => {
      // Vérifier la ref pour avoir la valeur actuelle (pas la closure)
      if (!isTodayRef.current) return;

      // Éviter d'écraser les modifications optimistes récentes (< 3 secondes)
      const timeSinceLastUpdate = Date.now() - lastUpdateTimestampRef.current;
      if (timeSinceLastUpdate < 3000) {
        console.log('⏸️ Polling ignoré - modification récente');
        return;
      }

      try {
        // FIREBASE DIRECT
        const response = await ordersService.getActive();
        const allOrders = response.orders || [];

        // FIX Build 260: Filtrer par date aujourd'hui (même filtre que loadOrders)
        const d = new Date();
        const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const extractDate = (val) => {
          if (!val) return '';
          const formatLocal = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
          if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}/)) return val.slice(0, 10);
          if (typeof val === 'string' && val.includes('T')) return formatLocal(new Date(val));
          if (typeof val === 'string') return val.slice(0, 10);
          if (val.toDate) return formatLocal(val.toDate());
          if (val.seconds) return formatLocal(new Date(val.seconds * 1000));
          if (val instanceof Date) return formatLocal(val);
          return '';
        };
        const filteredOrders = allOrders.filter(o => {
          const dateToCheck = o.pickup_date || o.created_at || o.order_date;
          return extractDate(dateToCheck) === todayStr;
        });

        const newOrders = filteredOrders.filter(o => o.status === 'new');

        const previousCount = previousNewOrdersCountRef.current;
        if (newOrders.length > previousCount && previousCount > 0) {
          playNotificationSound();
        }

        previousNewOrdersCountRef.current = newOrders.length;
        setOrders(filteredOrders);
      } catch (error) {
        console.error('Erreur polling:', error);
      }
    }, 5000); // 5 secondes - synchronisation plus rapide

    return () => clearInterval(interval);
  }, []); // Pas de dépendance - interval stable

  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.error('Erreur son:', error);
    }
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      // FIREBASE DIRECT: Si aujourd'hui → getActive(), sinon → getByDate()
      let response;
      if (isToday) {
        response = await ordersService.getActive();
      } else {
        response = await ordersService.getByDate(selectedDate);
      }
      const allOrders = response.orders || [];

      // FIX Build 260: Filtrer par date (aujourd'hui ou date sélectionnée)
      const targetDate = isToday
        ? (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })()
        : selectedDate;

      const extractDate = (val) => {
        if (!val) return '';
        const formatLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}/)) return val.slice(0, 10);
        if (typeof val === 'string' && val.includes('T')) return formatLocal(new Date(val));
        if (typeof val === 'string') return val.slice(0, 10);
        if (val.toDate) return formatLocal(val.toDate());
        if (val.seconds) return formatLocal(new Date(val.seconds * 1000));
        if (val instanceof Date) return formatLocal(val);
        return '';
      };

      const filteredOrders = allOrders.filter(o => {
        const dateToCheck = o.pickup_date || o.created_at || o.order_date;
        return extractDate(dateToCheck) === targetDate;
      });

      console.log(`📅 OrdersManagement: ${allOrders.length} total → ${filteredOrders.length} pour ${targetDate}`);
      setOrders(filteredOrders);
    } catch (error) {
      console.error('Erreur chargement:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const getNextStatus = (currentStatus, orderType) => {
    const flow = {
      'new': 'in_preparation',
      'in_preparation': 'ready',
      'ready': orderType === 'delivery' ? 'out_for_delivery' : 'completed',
      'out_for_delivery': 'completed'
    };
    return flow[currentStatus] || null;
  };

  const handleQuickStatusChange = async (order, newStatus) => {
    // Bloquer si on termine sans paiement
    if (newStatus === 'completed' && !isOrderPaid(order)) {
      toast.error('Paiement requis', 'Veuillez enregistrer le paiement avant de terminer la commande');
      setSelectedOrder(order);
      setShowPaymentModal(true);
      return;
    }

    setUpdatingOrder(order.id);

    // Sauvegarder l'état précédent pour rollback
    const previousStatus = order.status;

    try {
      // Mise à jour optimiste + marquer le timestamp pour bloquer le polling
      lastUpdateTimestampRef.current = Date.now();
      setOrders(prev => prev.map(o =>
        o.id === order.id ? { ...o, status: newStatus } : o
      ));

      // FIREBASE DIRECT
      await ordersService.updateStatus(order.id, newStatus);
    } catch (error) {
      console.error('Erreur changement statut:', error);
      toast.error('Erreur', 'Impossible de changer le statut de la commande');
      // Rollback vers état précédent (pas de reload complet)
      setOrders(prev => prev.map(o =>
        o.id === order.id ? { ...o, status: previousStatus } : o
      ));
    } finally {
      setUpdatingOrder(null);
    }
  };

  const handleCancelOrder = async (reason) => {
    if (!selectedOrder) return;

    // Sauvegarder l'état précédent pour rollback
    const previousStatus = selectedOrder.status;

    try {
      // Mise à jour optimiste + marquer le timestamp pour bloquer le polling
      lastUpdateTimestampRef.current = Date.now();
      setOrders(prev => prev.map(o =>
        o.id === selectedOrder.id ? { ...o, status: 'cancelled' } : o
      ));

      // FIREBASE DIRECT
      await ordersService.cancel(selectedOrder.id, reason);
      setShowCancellationModal(false);
      setSelectedOrder(null);
    } catch (error) {
      console.error('Erreur:', error);
      // Rollback vers état précédent
      setOrders(prev => prev.map(o =>
        o.id === selectedOrder.id ? { ...o, status: previousStatus } : o
      ));
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtrage des commandes (recherche + filtres)
  const filteredOrders = orders.filter(order => {
    // Filtre recherche
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        order.order_number?.toLowerCase().includes(query) ||
        order.customer_name?.toLowerCase().includes(query) ||
        order.customer_email?.toLowerCase().includes(query) ||
        order.customer_phone?.includes(query);
      if (!matchesSearch) return false;
    }
    // Filtre statut
    if (statusFilter !== 'all' && order.status !== statusFilter) return false;
    // Filtre type
    if (typeFilter !== 'all' && order.order_type !== typeFilter) return false;
    // Filtre paiement
    if (paymentFilter !== 'all') {
      if (paymentFilter === 'paid' && !isOrderPaid(order)) return false;
      if (paymentFilter === 'unpaid' && isOrderPaid(order)) return false;
    }
    return true;
  });

  const getOrdersForColumn = (status) => {
    return filteredOrders.filter(o => o.status === status);
  };

  // Export CSV
  const handleExportCSV = () => {
    const headers = ['Numéro', 'Date', 'Heure', 'Client', 'Téléphone', 'Total', 'Statut', 'Type', 'Paiement'];
    const rows = filteredOrders.map(o => [
      o.order_number || o.id?.slice(0, 8),
      new Date(o.created_at).toLocaleDateString('fr-FR'),
      new Date(o.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      o.customer_name || '',
      o.customer_phone || '',
      o.total?.toFixed(2) || '0.00',
      o.status,
      o.order_type,
      isOrderPaid(o) ? 'Payé' : 'Non payé'
    ]);

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell || ''}"`).join(';'))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `commandes_${selectedDate}.csv`;
    link.click();
    toast.success('Export réussi', `${filteredOrders.length} commandes exportées`);
  };

  // Impression commande
  const handlePrintOrder = (order) => {
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Commande #${order.order_number}</title>
          <style>
            body { font-family: 'Courier New', monospace; padding: 20px; max-width: 300px; margin: 0 auto; }
            .header { text-align: center; margin-bottom: 15px; border-bottom: 2px dashed #000; padding-bottom: 10px; }
            .header h1 { margin: 0; font-size: 24px; }
            .header h2 { margin: 5px 0; font-size: 16px; }
            .info { margin: 10px 0; font-size: 12px; }
            .info p { margin: 3px 0; }
            .items { margin: 15px 0; border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 10px 0; }
            .item { display: flex; justify-content: space-between; font-size: 12px; margin: 5px 0; }
            .total { font-weight: bold; font-size: 18px; text-align: right; margin-top: 10px; }
            .footer { text-align: center; margin-top: 20px; font-size: 10px; color: #666; }
            .mention-paiement { font-size: 30px; font-weight: 900; letter-spacing: 2px; text-align: center; padding: 6px 0; margin: 8px 0; border-top: 3px solid #000; border-bottom: 3px solid #000; }
            .mention-paiement.a-encaisser { border-top-width: 6px; border-bottom-width: 6px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Family's</h1>
            <h2>Commande #${order.order_number || order.id?.slice(0, 8)}</h2>
            <p>${new Date(order.created_at).toLocaleString('fr-FR')}</p>
          </div>
          <div class="info">
            <p><strong>Client:</strong> ${order.customer_name || 'N/A'}</p>
            <p><strong>Tél:</strong> ${order.customer_phone || 'N/A'}</p>
            <p><strong>Type:</strong> ${order.order_type === 'delivery' ? 'Livraison' : order.order_type === 'on_site' ? 'Sur place' : 'À emporter'}</p>
            <p><strong>Paiement:</strong> ${isOrderPaid(order) ? 'Paiement encaisse' : 'A encaisser au comptoir'}</p>
            <div class="mention-paiement ${isOrderPaid(order) ? 'paye' : 'a-encaisser'}">${isOrderPaid(order) ? 'PAYE' : 'PAS PAYE'}</div>
          </div>
          <div class="items">
            <p style="font-weight: bold; margin-bottom: 8px;">Articles:</p>
            ${order.items?.map(item => `
              <div class="item">
                <span>${item.quantity}x ${item.name}</span>
                <span>${((item.total_price != null ? item.total_price : (item.unit_price || item.price || item.base_price || 0) * (item.quantity || 1)) || 0).toFixed(2)}€</span>
              </div>
              ${item.options?.length ? item.options.map(opt => `<div class="item" style="padding-left: 15px; color: #666;">↳ ${opt.name}</div>`).join('') : ''}
            `).join('') || '<p>Aucun article</p>'}
          </div>
          <div class="total">TOTAL: ${order.total?.toFixed(2)}€</div>
          <div class="footer">
            <p>Merci de votre visite !</p>
            <p>Imprimé le ${new Date().toLocaleString('fr-FR')}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  // Calcul des totaux
  const todayTotal = orders
    .filter(o => o.status === 'completed' && isOrderPaid(o))
    .reduce((sum, o) => sum + (o.total || 0), 0);

  const pendingPayments = orders
    .filter(o => !isOrderPaid(o) && o.status !== 'cancelled')
    .length;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header compact */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-gray-800">📦 Commandes</h1>
            <p className="text-sm text-gray-500">Vue Kanban - Glissez ou cliquez pour changer le statut</p>
          </div>
          
          <div className="flex items-center gap-6">
            {/* Sélecteur de date */}
            <div className="flex items-center gap-2 bg-white border-2 border-gray-200 rounded-lg px-2 py-1">
              <button
                onClick={() => {
                  const d = new Date(selectedDate);
                  d.setDate(d.getDate() - 1);
                  setSelectedDate(d.toISOString().split('T')[0]);
                }}
                className="p-1 hover:bg-gray-100 rounded"
                title="Jour précédent"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="border-0 bg-transparent font-medium text-gray-700 cursor-pointer focus:outline-none"
                />
              </div>
              <button
                onClick={() => {
                  const d = new Date(selectedDate);
                  d.setDate(d.getDate() + 1);
                  const tomorrow = d.toISOString().split('T')[0];
                  const today = new Date().toISOString().split('T')[0];
                  if (tomorrow <= today) {
                    setSelectedDate(tomorrow);
                  }
                }}
                disabled={isToday}
                className={`p-1 rounded ${isToday ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                title="Jour suivant"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              {!isToday && (
                <button
                  onClick={() => setSelectedDate(new Date().toISOString().split('T')[0])}
                  className="ml-1 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  ↩ Retour aujourd'hui
                </button>
              )}
            </div>

            {/* Stats rapides */}
            <div className="flex items-center gap-4">
              <div className="text-center px-4 py-2 bg-green-50 rounded-lg">
                <p className="text-xs text-gray-500">CA {isToday ? "Aujourd'hui" : selectedDate}</p>
                <p className="text-xl font-black text-green-600">{todayTotal.toFixed(2)}€</p>
              </div>
              {pendingPayments > 0 && (
                <div className="text-center px-4 py-2 bg-orange-50 rounded-lg">
                  <p className="text-xs text-gray-500">À encaisser</p>
                  <p className="text-xl font-black text-orange-600">{pendingPayments}</p>
                </div>
              )}
            </div>

            <button
              onClick={loadOrders}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-white border-b px-6 py-3">
        <div className="flex flex-wrap items-center gap-4">
          {/* Recherche */}
          <div className="relative flex-1 min-w-[200px] max-w-[400px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher (numéro, nom, téléphone...)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filtre Statut */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
          >
            <option value="all">Tous les statuts</option>
            <option value="new">🆕 Nouvelles</option>
            <option value="in_preparation">👨‍🍳 En préparation</option>
            <option value="ready">✅ Prêtes</option>
            <option value="completed">🎉 Terminées</option>
            <option value="cancelled">❌ Annulées</option>
          </select>

          {/* Filtre Type */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
          >
            <option value="all">Tous les types</option>
            <option value="takeaway">🥡 À emporter</option>
            <option value="delivery">🚚 Livraison</option>
            <option value="on_site">🍽️ Sur place</option>
          </select>

          {/* Filtre Paiement */}
          <select
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
          >
            <option value="all">Tous paiements</option>
            <option value="paid">✅ Payées</option>
            <option value="unpaid">⚠️ Non payées</option>
          </select>

          {/* Bouton Reset filtres */}
          {(searchQuery || statusFilter !== 'all' || typeFilter !== 'all' || paymentFilter !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setTypeFilter('all');
                setPaymentFilter('all');
              }}
              className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Réinitialiser
            </button>
          )}

          {/* Compteur résultats */}
          <span className="text-sm text-gray-500">
            {filteredOrders.length} commande{filteredOrders.length > 1 ? 's' : ''}
          </span>

          {/* Bouton Export */}
          <button
            onClick={handleExportCSV}
            disabled={filteredOrders.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="p-4 h-[calc(100vh-160px)] overflow-x-auto">
        <div className="flex gap-4 h-full min-w-max">
          {COLUMNS.map((column) => {
            const columnOrders = getOrdersForColumn(column.status);
            const Icon = column.icon;
            
            return (
              <div 
                key={column.id}
                className={`flex-1 min-w-[320px] max-w-[400px] flex flex-col rounded-xl ${column.bgColor} border-2 ${column.borderColor}`}
              >
                {/* Header colonne */}
                <div className={`${column.headerBg} text-white p-4 rounded-t-lg`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className="w-6 h-6" />
                      <span className="font-bold text-lg">{column.label}</span>
                    </div>
                    <span className="bg-white/20 px-3 py-1 rounded-full font-black text-xl">
                      {columnOrders.length}
                    </span>
                  </div>
                </div>

                {/* Liste des commandes */}
                <div className="flex-1 overflow-y-auto p-3 space-y-3">
                  {columnOrders.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <p className="text-sm">Aucune commande</p>
                    </div>
                  ) : (
                    columnOrders.map((order) => (
                      <OrderCard
                        key={order.id}
                        order={order}
                        column={column}
                        isUpdating={updatingOrder === order.id}
                        onStatusChange={(newStatus) => handleQuickStatusChange(order, newStatus)}
                        onPayment={() => {
                          setSelectedOrder(order);
                          setShowPaymentModal(true);
                        }}
                        onCancel={() => {
                          setSelectedOrder(order);
                          setShowCancellationModal(true);
                        }}
                        onViewDetails={() => {
                          setSelectedOrder(order);
                          setShowDetailModal(true);
                        }}
                        onPrint={() => handlePrintOrder(order)}
                        getNextStatus={getNextStatus}
                        formatTime={formatTime}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modals */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => { setShowPaymentModal(false); setSelectedOrder(null); }}
        order={selectedOrder}
        onSuccess={() => { loadOrders(); setShowPaymentModal(false); setSelectedOrder(null); }}
      />

      <CancellationModal
        isOpen={showCancellationModal}
        onClose={() => { setShowCancellationModal(false); setSelectedOrder(null); }}
        order={selectedOrder}
        onConfirm={handleCancelOrder}
      />

      <RefundModal
        isOpen={showRefundModal}
        onClose={() => { setShowRefundModal(false); setSelectedOrder(null); }}
        order={selectedOrder}
        onSuccess={() => { loadOrders(); setShowRefundModal(false); setSelectedOrder(null); }}
      />

      {/* Modal Détail */}
      {showDetailModal && selectedOrder && (
        <DetailModal
          order={selectedOrder}
          onClose={() => { setShowDetailModal(false); setSelectedOrder(null); }}
          onStatusChange={(newStatus) => {
            handleQuickStatusChange(selectedOrder, newStatus);
            setShowDetailModal(false);
            setSelectedOrder(null);
          }}
          onPayment={() => {
            setShowDetailModal(false);
            setShowPaymentModal(true);
          }}
          getNextStatus={getNextStatus}
          formatDate={formatDate}
        />
      )}
    </div>
  );
};

// Composant Carte Commande
const OrderCard = ({ order, column, isUpdating, onStatusChange, onPayment, onCancel, onViewDetails, onPrint, getNextStatus, formatTime }) => {
  const nextStatus = getNextStatus(order.status, order.order_type);
  const isPaid = isOrderPaid(order);
  
  return (
    <div 
      className={`bg-white rounded-xl shadow-md border-2 transition-all hover:shadow-xl ${
        isUpdating ? 'opacity-50 scale-95' : 'hover:scale-[1.02]'
      } ${!isPaid ? 'border-orange-400' : 'border-transparent'}`}
    >
      {/* Header carte */}
      <div 
        className="p-3 cursor-pointer"
        onClick={onViewDetails}
      >
        {/* Numéro + Heure */}
        <div className="flex items-center justify-between mb-2">
          <span className="font-black text-lg text-gray-800">
            #{order.order_number || order.id?.slice(0, 6)}
          </span>
          <span className="text-sm text-gray-500 font-medium">
            🕐 {formatTime(order.created_at)}
          </span>
        </div>

        {/* Client */}
        <div className="flex items-center gap-2 mb-2">
          <User className="w-4 h-4 text-gray-400" />
          <span className="font-semibold text-gray-700 truncate">
            {order.customer_name || 'Client'}
          </span>
          {order.customer_phone && (
            <a 
              href={`tel:${order.customer_phone}`}
              onClick={(e) => e.stopPropagation()}
              className="text-blue-500 hover:text-blue-700"
            >
              <Phone className="w-4 h-4" />
            </a>
          )}
        </div>

        {/* Type + Total */}
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-bold px-2 py-1 rounded-full ${
            order.order_type === 'delivery'
              ? 'bg-blue-100 text-blue-700'
              : order.order_type === 'on_site'
                ? 'bg-green-100 text-green-700'
                : 'bg-purple-100 text-purple-700'
          }`}>
            {order.order_type === 'delivery' ? '🚚 Livraison' : order.order_type === 'on_site' ? '🍽️ Sur place' : '🥡 À emporter'}
          </span>
          <span className="text-xl font-black text-gray-800">
            {order.total?.toFixed(2)}€
          </span>
        </div>

        {/* Paiement */}
        {!isPaid && (
          <div className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-1 rounded-full text-center mb-2">
            ⚠️ PAIEMENT EN ATTENTE
          </div>
        )}

        {/* Articles (résumé) */}
        <div className="text-xs text-gray-500">
          {order.items?.slice(0, 2).map((item, i) => (
            <span key={i}>
              {item.quantity}x {item.name?.substring(0, 15)}{item.name?.length > 15 ? '...' : ''}
              {i < Math.min(order.items.length, 2) - 1 ? ', ' : ''}
            </span>
          ))}
          {order.items?.length > 2 && (
            <span className="text-gray-400"> +{order.items.length - 2} autres</span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="border-t p-2 flex gap-2">
        {/* Bouton action principale */}
        {nextStatus && (
          <button
            onClick={() => onStatusChange(nextStatus)}
            disabled={isUpdating}
            className={`flex-1 py-2 px-3 rounded-lg font-bold text-white text-sm transition-all ${
              isUpdating 
                ? 'bg-gray-400' 
                : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-md hover:shadow-lg'
            }`}
          >
            {isUpdating ? (
              <Loader className="w-4 h-4 animate-spin mx-auto" />
            ) : (
              <>
                {column.nextAction} <ArrowRight className="w-4 h-4 inline ml-1" />
              </>
            )}
          </button>
        )}

        {/* Bouton paiement si non payé */}
        {!isPaid && (
          <button
            onClick={onPayment}
            className="py-2 px-3 rounded-lg font-bold text-green-600 bg-green-50 hover:bg-green-100 text-sm transition-all"
          >
            <CreditCard className="w-4 h-4" />
          </button>
        )}

        {/* Bouton annulation - disponible même pour les commandes terminées */}
        {order.status !== 'cancelled' && (
          <button
            onClick={onCancel}
            className="py-2 px-3 rounded-lg font-bold text-red-600 bg-red-50 hover:bg-red-100 text-sm transition-all"
            title={order.status === 'completed' ? 'Annuler cette commande terminée' : 'Annuler'}
          >
            <XCircle className="w-4 h-4" />
          </button>
        )}

        {/* Bouton impression */}
        <button
          onClick={onPrint}
          className="py-2 px-3 rounded-lg font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 text-sm transition-all"
          title="Imprimer le ticket"
        >
          <Printer className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Modal Détail Commande
const DetailModal = ({ order, onClose, onStatusChange, onPayment, getNextStatus, formatDate }) => {
  const nextStatus = getNextStatus(order.status, order.order_type);
  const isPaid = isOrderPaid(order);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-primary to-red-600 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-black">
                #{order.order_number || order.id?.slice(0, 8)}
              </h2>
              <p className="text-white/80 text-sm">{formatDate(order.created_at)}</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Client */}
          <div className="bg-blue-50 p-4 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-black text-xl">
                {(order.customer_name || 'C')[0].toUpperCase()}
              </div>
              <div className="flex-1">
                <p className="font-bold text-lg text-gray-800">{order.customer_name || 'Client'}</p>
                {order.customer_phone && (
                  <a href={`tel:${order.customer_phone}`} className="text-blue-600 text-sm flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {order.customer_phone}
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Type */}
          <div className={`p-4 rounded-xl ${
            order.order_type === 'delivery' ? 'bg-blue-50' : order.order_type === 'on_site' ? 'bg-green-50' : 'bg-purple-50'
          }`}>
            <div className="flex items-center gap-2">
              {order.order_type === 'delivery' ? (
                <>
                  <Truck className="w-6 h-6 text-blue-600" />
                  <span className="font-bold text-blue-700">Livraison</span>
                </>
              ) : order.order_type === 'on_site' ? (
                <>
                  <span className="text-2xl">🍽️</span>
                  <span className="font-bold text-green-700">Sur place</span>
                </>
              ) : (
                <>
                  <Package className="w-6 h-6 text-purple-600" />
                  <span className="font-bold text-purple-700">À emporter</span>
                </>
              )}
              {(order.pickup_time || order.delivery_time) && (
                <span className="ml-auto text-gray-600 font-medium">
                  🕐 {order.pickup_time || order.delivery_time}
                </span>
              )}
            </div>
          </div>

          {/* Articles */}
          <div>
            <h3 className="font-bold text-gray-700 mb-3">🍽️ Articles</h3>
            <div className="space-y-2">
              {order.items?.map((item, idx) => {
                const isBogo = item.fromBogo || item.from_bogo;
                const isReward = item.fromReward || item.from_reward;
                const isFree = (item.total_price === 0 || item.price === 0) || isBogo || isReward;

                return (
                  <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${isFree ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 ${isFree ? 'bg-green-500' : 'bg-primary'} text-white rounded-full flex items-center justify-center font-bold text-sm`}>
                        {item.quantity}
                      </span>
                      <div>
                        <p className={`font-semibold ${isFree ? 'text-green-700' : 'text-gray-800'}`}>
                          {isFree && '🎁 '}{item.name}
                          {isBogo && <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">1+1</span>}
                          {isReward && !isBogo && <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">Cadeau</span>}
                          {isFree && !isBogo && !isReward && <span className="ml-2 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">Offert</span>}
                        </p>
                        {item.notes && (
                          <p className="text-xs text-gray-500">📝 {item.notes}</p>
                        )}
                      </div>
                    </div>
                    <span className={`font-bold ${isFree ? 'text-green-600' : 'text-gray-800'}`}>
                      {isFree ? '0.00€' : `${(item.total_price || 0).toFixed(2)}€`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Notes client - EN ROUGE pour visibilité */}
          {order.notes && (
            <div className="p-4 bg-red-50 border-2 border-red-300 rounded-xl">
              <p className="text-red-700 font-bold text-lg">
                📝 NOTE CLIENT : {order.notes}
              </p>
            </div>
          )}

          {/* Total + Paiement */}
          <div className={`p-4 rounded-xl ${isPaid ? 'bg-green-50' : 'bg-orange-50'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isPaid ? (
                  <>
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <span className="font-bold text-green-700">Payé</span>
                    <span className="text-sm text-gray-500">({order.payment_method})</span>
                  </>
                ) : (
                  <>
                    <Clock className="w-6 h-6 text-orange-600" />
                    <span className="font-bold text-orange-700">À payer</span>
                  </>
                )}
              </div>
              <span className="text-3xl font-black text-gray-800">{order.total?.toFixed(2)}€</span>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3 pt-4">
            {!isPaid && (
              <button
                onClick={onPayment}
                className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all"
              >
                <CreditCard className="w-5 h-5 inline mr-2" />
                Enregistrer le paiement
              </button>
            )}

            {nextStatus && (
              <button
                onClick={() => onStatusChange(nextStatus)}
                className="w-full py-4 bg-gradient-to-r from-primary to-red-600 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all"
              >
                {nextStatus === 'in_preparation' && '🔥 Commencer la préparation'}
                {nextStatus === 'ready' && '✅ Marquer comme prête'}
                {nextStatus === 'out_for_delivery' && '🚚 Partir en livraison'}
                {nextStatus === 'completed' && '🎉 Terminer la commande'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrdersManagement;
